import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { authApi, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, type AuthFailure } from "../api/auth";
import { PublicShell } from "../design/components/PublicShell";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { TextField } from "../design/components/TextField";
import "../design/components/button.css";
import { useT } from "../i18n/locale";
import { describeAuthFailure } from "./auth-failure";
import "./auth.css";

type PasswordProblem = "password_too_short" | "password_too_long";
type Phase = "editing" | "submitting" | "done" | "invalid_link";

/** Il token del link di `create-platform-admin` (`buildPasswordResetUrl`), codificato dopo `#`. */
function readTokenFromHash(hash: string): string | null {
  if (hash.length <= 1) return null;
  try {
    return decodeURIComponent(hash.slice(1)) || null;
  } catch {
    return null;
  }
}

function passwordProblem(password: string): PasswordProblem | null {
  if (password.length < PASSWORD_MIN_LENGTH) return "password_too_short";
  if (password.length > PASSWORD_MAX_LENGTH) return "password_too_long";
  return null;
}

export function ResetPasswordPage() {
  const t = useT();
  // Il token si legge una volta sola e poi sparisce dalla barra degli indirizzi (effetto qui sotto):
  // resta soltanto nello stato del componente, non nella cronologia, nei preferiti o in uno screenshot.
  const [token] = useState(() => readTokenFromHash(window.location.hash));
  const [phase, setPhase] = useState<Phase>(() => (token ? "editing" : "invalid_link"));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // Gli errori sono codici e non testi, così seguono il cambio di lingua.
  const [problem, setProblem] = useState<PasswordProblem | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.location.hash === "") return;
    const { pathname, search } = window.location;
    // Si conserva `history.state`, dove il router tiene la propria posizione nella cronologia.
    window.history.replaceState(window.history.state, "", `${pathname}${search}`);
  }, []);

  // Quando il modulo lascia il posto a un esito, il focus va sull'esito, che così viene letto.
  useEffect(() => {
    if (phase === "done" || phase === "invalid_link") resultRef.current?.focus();
  }, [phase]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (phase !== "editing" || !token) return;
    const tooWeak = passwordProblem(password);
    const differs = password !== confirm;
    setProblem(tooWeak);
    setMismatch(!tooWeak && differs);
    setFailure(null);
    if (tooWeak) {
      passwordRef.current?.focus();
      return;
    }
    if (differs) {
      confirmRef.current?.focus();
      return;
    }
    setPhase("submitting");
    const result = await authApi.resetPassword({ token, newPassword: password });
    if (result.ok) {
      setPhase("done");
    } else if (result.failure === "invalid_link") {
      setPhase("invalid_link");
    } else if (result.failure === "password_too_short" || result.failure === "password_too_long") {
      setPhase("editing");
      setProblem(result.failure);
    } else {
      setPhase("editing");
      setFailure(result.failure);
    }
  }

  // Il focus torna dove si può correggere quando il pulsante, disabilitato durante l'invio, si riattiva.
  useEffect(() => {
    if (phase !== "editing") return;
    if (problem) passwordRef.current?.focus();
    else if (failure) submitRef.current?.focus();
  }, [phase, problem, failure]);

  const submitting = phase === "submitting";

  return (
    <PublicShell>
      <Card className="g-stack">
        <h1 id="reset-title">{t("auth.resetPassword.title")}</h1>
        {phase === "done" || phase === "invalid_link" ? (
          <div ref={resultRef} className="g-stack g-auth__result" tabIndex={-1} role="status">
            <p className="g-auth__message" data-tone={phase === "done" ? "success" : "error"}>
              {phase === "done" ? t("auth.resetPassword.done") : t("auth.resetPassword.invalidLink")}
            </p>
            <div className="g-auth__actions">
              <Link to="/login" className="g-button" data-variant={phase === "done" ? "primary" : "secondary"}>
                {t("auth.resetPassword.goToLogin")}
              </Link>
            </div>
          </div>
        ) : (
          <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-labelledby="reset-title" aria-busy={submitting} noValidate>
            <p className="g-auth__muted">{t("auth.resetPassword.intro")}</p>
            <TextField
              id="new-password"
              ref={passwordRef}
              label={t("auth.resetPassword.newPassword", { min: PASSWORD_MIN_LENGTH })}
              type="password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
              value={password}
              error={problem ? describeAuthFailure(t, problem) : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              id="confirm-password"
              ref={confirmRef}
              label={t("auth.resetPassword.confirmPassword")}
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              error={mismatch ? t("auth.resetPassword.mismatch") : undefined}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {failure ? (
              <p className="g-auth__message" data-tone="error" role="alert">
                {describeAuthFailure(t, failure)}
              </p>
            ) : null}
            <Button ref={submitRef} variant="primary" type="submit" disabled={submitting}>
              {submitting ? t("auth.resetPassword.submitting") : t("auth.resetPassword.submit")}
            </Button>
          </form>
        )}
      </Card>
    </PublicShell>
  );
}
